/**
 * PropsPanel — Tabbed right-side properties panel for MapEditorScene.
 *
 * Three tabs: Map (name/difficulty/waves/enemies), Cinematic (triggers), File (export/import/generate/save).
 * Each tab has its own scrollable container with geometry mask clipping.
 */
import Phaser from 'phaser';
import { EnemyType, EnemyGrade, ENEMY_STATS, GRADE_MODIFIERS } from '@/config/GameConfig';
import { CinematicTrigger } from '@/map/MapDefs';
import { getSequenceIds } from '@/cinematic/CinematicDirector';
import { TECH, drawTechRect } from '@/ui/TechUI';
import { SoundManager } from '@/systems/SoundManager';
import { t } from '@/i18n/I18n';

// ── Types ──

export interface PropsPanelCallbacks {
  promptName(): void;
  cycleDifficulty(dir: number): void;
  adjustWaveCount(delta: number): void;
  toggleEnemy(type: EnemyType, cb: Phaser.GameObjects.Rectangle): void;
  toggleGrade(grade: EnemyGrade, cb: Phaser.GameObjects.Rectangle): void;
  exportMap(): void;
  exportClipboard(): void;
  importMap(): void;
  importClipboard(): void;
  clearAll(): void;
  cycleComplexity(dir: number): void;
  cycleRichness(dir: number): void;
  cycleHazards(dir: number): void;
  cycleIntensity(dir: number): void;
  generateProceduralMap(): void;
  saveToCampaign(): void;
  resetToFactoryMap(): void;
  saveToMapList(): void;
  setStatus(text: string): void;
  pickTile(callback: (col: number, row: number) => void): void;
  getMapWaveCount(): number;
  getGridSize(): { cols: number; rows: number };
}

const EVENT_TYPES: CinematicTrigger['event'][] = [
  'mapStart', 'waveStart', 'waveEnd', 'victory', 'defeat', 'tileEnter',
];

export interface PropsPanelOptions {
  propsX: number;
  propsW: number;
  canvasH: number;
  campaignLevelId: string | null;
  mapName: string;
  mapDifficulty: string;
  mapWaveCount: number;
  complexityLabel: string;
  richnessLabel: string;
  hazardsLabel: string;
  intensityLabel: string;
  allowedEnemies: Set<EnemyType>;
  allowedGrades: Set<EnemyGrade>;
  allEnemyTypes: EnemyType[];
  cinematicTriggers: CinematicTrigger[];
}

// Single scrollable panel (no tabs)
const CONTENT_Y = 4;

export class PropsPanel {
  private scene: Phaser.Scene;
  private callbacks: PropsPanelCallbacks;
  private opts: PropsPanelOptions;

  private container!: Phaser.GameObjects.Container;
  private scrollY = 0;
  private contentHeight = 0;
  private visibleH = 0;
  private maskShape!: Phaser.GameObjects.Graphics;

  // Dynamic UI refs (public for scene to update on import/load)
  public nameText!: Phaser.GameObjects.Text;
  public diffText!: Phaser.GameObjects.Text;
  public waveText!: Phaser.GameObjects.Text;
  public complexityText!: Phaser.GameObjects.Text;
  public richnessText!: Phaser.GameObjects.Text;
  public hazardsText!: Phaser.GameObjects.Text;
  public intensityText!: Phaser.GameObjects.Text;
  public enemyCheckboxes: Map<EnemyType, Phaser.GameObjects.Rectangle> = new Map();
  public gradeCheckboxes: Map<EnemyGrade, Phaser.GameObjects.Rectangle> = new Map();

  // Cinematic trigger card expansion state (indices of expanded cards)
  private expandedTriggers: Set<number> = new Set();

  constructor(scene: Phaser.Scene, callbacks: PropsPanelCallbacks, opts: PropsPanelOptions) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.opts = opts;
    this.visibleH = opts.canvasH - CONTENT_Y;
  }

  build(): void {
    const { propsX, propsW, canvasH } = this.opts;

    // Background
    this.scene.add.rectangle(propsX + propsW / 2, canvasH / 2, propsW, canvasH, 0x111122, 0.95)
      .setStrokeStyle(1, 0x333355).setDepth(10);

    // Mask for scrollable content area
    this.maskShape = this.scene.add.graphics();
    this.maskShape.fillStyle(0xffffff);
    this.maskShape.fillRect(propsX, CONTENT_Y, propsW, canvasH - CONTENT_Y);
    this.maskShape.setVisible(false);
    const geoMask = this.maskShape.createGeometryMask();

    // Single scrollable container
    this.container = this.scene.add.container(0, 0).setDepth(10);
    this.container.setMask(geoMask);

    this.buildAllSections();
  }

  // ══════════════════════════════════════════
  //  SCROLL
  // ══════════════════════════════════════════

  scrollActiveTab(dy: number): void {
    const maxScroll = Math.max(0, this.contentHeight - this.visibleH);
    this.scrollY = Phaser.Math.Clamp(this.scrollY + dy * 0.5, 0, maxScroll);
    this.container.setY(-this.scrollY);
  }

  // ══════════════════════════════════════════
  //  ALL SECTIONS (single scrollable panel)
  // ══════════════════════════════════════════

  private buildAllSections(): void {
    const { propsX, propsW } = this.opts;
    const c = this.container;
    let y = CONTENT_Y + 8;

    y = this.buildMapSection(c, y);
    y = this.buildCinematicSection(c, y);
    y = this.buildFileSection(c, y);

    this.contentHeight = y + 10;
  }

  /** Rebuild the entire panel (called when cinematic triggers change) */
  rebuildCinematicContent(): void {
    this.container.removeAll(true);
    this.enemyCheckboxes.clear();
    this.gradeCheckboxes.clear();
    const c = this.container;
    const { propsX, propsW } = this.opts;
    let y = CONTENT_Y + 8;
    y = this.buildMapSection(c, y);
    y = this.buildCinematicSection(c, y);
    y = this.buildFileSection(c, y);
    this.contentHeight = y + 10;

    // Clamp scroll if content shrunk
    const maxScroll = Math.max(0, this.contentHeight - this.visibleH);
    if (this.scrollY > maxScroll) {
      this.scrollY = maxScroll;
      this.container.setY(-this.scrollY);
    }
  }

  // ══════════════════════════════════════════
  //  MAP SECTION
  // ══════════════════════════════════════════

  private buildMapSection(c: Phaser.GameObjects.Container, y: number): number {
    const { propsX, propsW } = this.opts;

    this.addText(c, propsX + 10, y, 'MAP PROPERTIES', {
      fontSize: '11px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    });
    y += 20;

    // Name
    this.addText(c, propsX + 10, y, 'Name:', { fontSize: '10px', color: '#aaaaaa', fontFamily: 'monospace' });
    y += 14;
    this.nameText = this.addText(c, propsX + 10, y, this.opts.mapName, {
      fontSize: '11px', color: '#ffffff', fontFamily: 'monospace',
    });
    y += 16;
    this.addButton(c, propsX + 10, y, 90, 18, 'Edit Name', () => this.callbacks.promptName());
    y += 28;

    // Difficulty
    this.addText(c, propsX + 10, y, 'Difficulty:', { fontSize: '10px', color: '#aaaaaa', fontFamily: 'monospace' });
    y += 14;
    this.diffText = this.addText(c, propsX + 10, y, this.opts.mapDifficulty, {
      fontSize: '11px', color: '#ffffff', fontFamily: 'monospace',
    });
    y += 16;
    this.addButton(c, propsX + 10, y, 40, 18, '◄', () => this.callbacks.cycleDifficulty(-1));
    this.addButton(c, propsX + 55, y, 40, 18, '►', () => this.callbacks.cycleDifficulty(1));
    y += 28;

    // Wave count
    this.addText(c, propsX + 10, y, 'Waves:', { fontSize: '10px', color: '#aaaaaa', fontFamily: 'monospace' });
    y += 14;
    this.waveText = this.addText(c, propsX + 10, y, `${this.opts.mapWaveCount}`, {
      fontSize: '11px', color: '#ffffff', fontFamily: 'monospace',
    });
    y += 16;
    this.addButton(c, propsX + 10, y, 40, 18, '−10', () => this.callbacks.adjustWaveCount(-10));
    this.addButton(c, propsX + 55, y, 40, 18, '+10', () => this.callbacks.adjustWaveCount(10));
    y += 22;
    this.addButton(c, propsX + 10, y, 40, 18, '−1', () => this.callbacks.adjustWaveCount(-1));
    this.addButton(c, propsX + 55, y, 40, 18, '+1', () => this.callbacks.adjustWaveCount(1));
    y += 24;

    // Intensity
    this.addText(c, propsX + 10, y, 'Intensity:', { fontSize: '10px', color: '#aaaaaa', fontFamily: 'monospace' });
    y += 14;
    this.intensityText = this.addText(c, propsX + 10, y, this.opts.intensityLabel, {
      fontSize: '11px', color: '#ffffff', fontFamily: 'monospace',
    });
    this.addButton(c, propsX + 100, y - 2, 40, 18, '◄', () => this.callbacks.cycleIntensity(-1));
    this.addButton(c, propsX + 145, y - 2, 40, 18, '►', () => this.callbacks.cycleIntensity(1));
    y += 24;

    // Enemies
    this.addText(c, propsX + 10, y, 'Enemies:', { fontSize: '10px', color: '#aaaaaa', fontFamily: 'monospace' });
    y += 16;

    for (const et of this.opts.allEnemyTypes) {
      const label = ENEMY_STATS[et].label;
      const isOn = this.opts.allowedEnemies.has(et);
      const cb = this.scene.add.rectangle(propsX + 18, y + 6, 12, 12, isOn ? 0x44cc44 : 0x333355)
        .setStrokeStyle(1, 0x666688)
        .setInteractive({ useHandCursor: true });
      cb.on('pointerdown', () => {
        if (!this.isInVisibleArea(cb)) return;
        this.callbacks.toggleEnemy(et, cb);
      });
      this.enemyCheckboxes.set(et, cb);
      c.add(cb);

      this.addText(c, propsX + 30, y, label, {
        fontSize: '10px', color: '#cccccc', fontFamily: 'monospace',
      });
      y += 20;
    }

    // Grades
    y += 8;
    this.addText(c, propsX + 10, y, 'Grades:', { fontSize: '10px', color: '#aaaaaa', fontFamily: 'monospace' });
    y += 16;

    const ALL_GRADES = [EnemyGrade.ELITE, EnemyGrade.BOSS, EnemyGrade.SUPER, EnemyGrade.ULTRA, EnemyGrade.GOLD];
    for (const g of ALL_GRADES) {
      const label = GRADE_MODIFIERS[g]?.label ?? g;
      const isOn = this.opts.allowedGrades.has(g);
      const cb = this.scene.add.rectangle(propsX + 18, y + 6, 12, 12, isOn ? 0xcc8844 : 0x333355)
        .setStrokeStyle(1, 0x666688)
        .setInteractive({ useHandCursor: true });
      cb.on('pointerdown', () => {
        if (!this.isInVisibleArea(cb)) return;
        this.callbacks.toggleGrade(g, cb);
      });
      this.gradeCheckboxes.set(g, cb);
      c.add(cb);

      this.addText(c, propsX + 30, y, label, {
        fontSize: '10px', color: GRADE_MODIFIERS[g]?.labelColor ?? '#cccccc', fontFamily: 'monospace',
      });
      y += 20;
    }

    // Separator
    y += 10;
    const sep = this.scene.add.rectangle(propsX + propsW / 2, y, propsW - 20, 1, 0x333355);
    c.add(sep);
    y += 14;

    return y;
  }

  // ══════════════════════════════════════════
  //  CINEMATIC SECTION
  // ══════════════════════════════════════════

  private buildCinematicSection(c: Phaser.GameObjects.Container, y: number): number {
    const { propsX, propsW } = this.opts;

    this.addText(c, propsX + 10, y, 'CINEMATICS', {
      fontSize: '11px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    });
    y += 20;

    const triggers = this.opts.cinematicTriggers;
    for (let i = 0; i < triggers.length; i++) {
      const trigger = triggers[i];
      const expanded = this.expandedTriggers.has(i);
      y = this.buildTriggerCard(c, triggers, trigger, i, expanded, y);
    }

    // Add trigger button
    this.addButton(c, propsX + 10, y, propsW - 20, 22, '+ Add Trigger', () => {
      const seqIds = getSequenceIds();
      if (seqIds.length === 0) {
        this.callbacks.setStatus('No cinematic sequences available. Create one in the Cinematic Editor first.');
        return;
      }
      const newIdx = triggers.length;
      triggers.push({ sequenceId: seqIds[0], event: 'mapStart', once: true });
      this.expandedTriggers.add(newIdx);
      this.rebuildCinematicContent();
    });
    y += 32;

    // Separator
    const sep = this.scene.add.rectangle(propsX + propsW / 2, y, propsW - 20, 1, 0x333355);
    c.add(sep);
    y += 14;

    return y;
  }

  /** Build a single expandable trigger card. Returns the next Y position. */
  private buildTriggerCard(
    container: Phaser.GameObjects.Container,
    triggers: CinematicTrigger[],
    trigger: CinematicTrigger,
    idx: number,
    expanded: boolean,
    startY: number,
  ): number {
    const { propsX, propsW } = this.opts;
    const cardX = propsX + 6;
    const cardW = propsW - 12;
    let y = startY;

    // ── Header row: arrow + summary + delete ──
    const arrow = expanded ? '▼' : '▶';
    const summary = `${trigger.event}${trigger.waveNumber != null ? ` w${trigger.waveNumber}` : ''} → ${trigger.sequenceId}`;
    const headerLabel = `${arrow} ${summary}`;

    const headerText = this.scene.add.text(cardX + 4, y + 2, headerLabel, {
      fontSize: '9px', color: expanded ? '#ffcc00' : '#cccc88', fontFamily: 'monospace',
      wordWrap: { width: cardW - 30 },
    }).setInteractive({ useHandCursor: true });
    headerText.on('pointerover', () => headerText.setColor('#ffffff'));
    headerText.on('pointerout', () => headerText.setColor(expanded ? '#ffcc00' : '#cccc88'));
    headerText.on('pointerdown', () => {
      if (!this.isInVisibleArea(headerText)) return;
      SoundManager.play('click');
      if (this.expandedTriggers.has(idx)) {
        this.expandedTriggers.delete(idx);
      } else {
        this.expandedTriggers.add(idx);
      }
      this.rebuildCinematicContent();
    });
    container.add(headerText);

    // Delete button
    const del = this.scene.add.text(cardX + cardW - 14, y + 2, '✕', {
      fontSize: '9px', color: '#553333', fontFamily: 'monospace',
    }).setInteractive({ useHandCursor: true });
    del.on('pointerover', () => del.setColor('#ff4444'));
    del.on('pointerout', () => del.setColor('#553333'));
    del.on('pointerdown', () => {
      if (!this.isInVisibleArea(del)) return;
      SoundManager.play('click');
      triggers.splice(idx, 1);
      // Fix expanded indices after removal
      const newExpanded = new Set<number>();
      for (const ei of this.expandedTriggers) {
        if (ei < idx) newExpanded.add(ei);
        else if (ei > idx) newExpanded.add(ei - 1);
        // ei === idx is removed
      }
      this.expandedTriggers = newExpanded;
      this.rebuildCinematicContent();
    });
    container.add(del);
    y += 20;

    if (!expanded) {
      y += 4; // spacing between collapsed cards
      return y;
    }

    // ── Expanded fields ──
    const indent = cardX + 10;
    const fieldW = cardW - 20;

    // Card background
    const cardBg = this.scene.add.graphics();
    cardBg.fillStyle(0x1a1a2e, 0.6);
    const bgY = y;

    // Event type cycle selector
    y = this.addCycleSelector(container, indent, y, fieldW, 'Event', trigger.event, () => {
      const curIdx = EVENT_TYPES.indexOf(trigger.event);
      trigger.event = EVENT_TYPES[(curIdx - 1 + EVENT_TYPES.length) % EVENT_TYPES.length];
      this.cleanTriggerFields(trigger);
      this.rebuildCinematicContent();
    }, () => {
      const curIdx = EVENT_TYPES.indexOf(trigger.event);
      trigger.event = EVENT_TYPES[(curIdx + 1) % EVENT_TYPES.length];
      this.cleanTriggerFields(trigger);
      this.rebuildCinematicContent();
    });

    // Sequence ID cycle selector
    const seqIds = getSequenceIds();
    const seqDisplay = trigger.sequenceId.length > 16
      ? trigger.sequenceId.slice(0, 14) + '..'
      : trigger.sequenceId;
    y = this.addCycleSelector(container, indent, y, fieldW, 'Seq', seqDisplay, () => {
      if (seqIds.length === 0) return;
      const curIdx = seqIds.indexOf(trigger.sequenceId);
      trigger.sequenceId = seqIds[(curIdx - 1 + seqIds.length) % seqIds.length];
      this.rebuildCinematicContent();
    }, () => {
      if (seqIds.length === 0) return;
      const curIdx = seqIds.indexOf(trigger.sequenceId);
      trigger.sequenceId = seqIds[(curIdx + 1) % seqIds.length];
      this.rebuildCinematicContent();
    });

    // Wave number (only for waveStart/waveEnd)
    if (trigger.event === 'waveStart' || trigger.event === 'waveEnd') {
      const maxWaves = this.callbacks.getMapWaveCount();
      y = this.addCycleSelector(container, indent, y, fieldW, 'Wave',
        trigger.waveNumber != null ? `${trigger.waveNumber}` : 'any', () => {
          if (trigger.waveNumber == null) { trigger.waveNumber = maxWaves; }
          else if (trigger.waveNumber <= 1) { delete trigger.waveNumber; } // cycle to "any"
          else { trigger.waveNumber--; }
          this.rebuildCinematicContent();
        }, () => {
          if (trigger.waveNumber == null) { trigger.waveNumber = 1; }
          else if (trigger.waveNumber >= maxWaves) { delete trigger.waveNumber; } // cycle to "any"
          else { trigger.waveNumber++; }
          this.rebuildCinematicContent();
        });
    }

    // Tile (only for tileEnter)
    if (trigger.event === 'tileEnter') {
      const gridSize = this.callbacks.getGridSize();
      const tile = trigger.tile ?? { col: 0, row: 0 };
      trigger.tile = tile;

      // Col stepper
      y = this.addCycleSelector(container, indent, y, fieldW, 'Col', `${tile.col}`, () => {
        tile.col = Math.max(0, tile.col - 1);
        this.rebuildCinematicContent();
      }, () => {
        tile.col = Math.min(gridSize.cols - 1, tile.col + 1);
        this.rebuildCinematicContent();
      });

      // Row stepper
      y = this.addCycleSelector(container, indent, y, fieldW, 'Row', `${tile.row}`, () => {
        tile.row = Math.max(0, tile.row - 1);
        this.rebuildCinematicContent();
      }, () => {
        tile.row = Math.min(gridSize.rows - 1, tile.row + 1);
        this.rebuildCinematicContent();
      });

      // Pick tile button
      this.addButton(container, indent, y, fieldW, 18, 'Pick from Map', () => {
        this.callbacks.setStatus('Click a tile on the map...');
        this.callbacks.pickTile((col, row) => {
          tile.col = col;
          tile.row = row;
          this.callbacks.setStatus(`Tile set to (${col}, ${row})`);
          this.rebuildCinematicContent();
        });
      });
      y += 24;
    }

    // Once toggle
    y = this.addCheckbox(container, indent, y, 'Once', trigger.once !== false, () => {
      trigger.once = trigger.once === false ? true : false;
      this.rebuildCinematicContent();
    });

    // Draw card background now that we know height
    cardBg.fillRoundedRect(cardX, bgY - 2, cardW, y - bgY + 4, 3);
    cardBg.lineStyle(1, 0x333355, 0.5);
    cardBg.strokeRoundedRect(cardX, bgY - 2, cardW, y - bgY + 4, 3);
    container.add(cardBg);
    container.sendToBack(cardBg);

    y += 8; // spacing after expanded card
    return y;
  }

  /** Clean up fields that don't apply to the current event type. */
  private cleanTriggerFields(trigger: CinematicTrigger): void {
    if (trigger.event !== 'waveStart' && trigger.event !== 'waveEnd') {
      delete trigger.waveNumber;
    } else if (trigger.waveNumber == null) {
      // Keep "any" as default — don't force a wave number
    }
    if (trigger.event !== 'tileEnter') {
      delete trigger.tile;
    } else if (!trigger.tile) {
      trigger.tile = { col: 0, row: 0 };
    }
  }

  /** Add a ◄ value ► cycle selector row. Returns next Y. */
  private addCycleSelector(
    container: Phaser.GameObjects.Container,
    x: number, y: number, w: number,
    label: string, value: string,
    onPrev: () => void, onNext: () => void,
  ): number {
    // Label
    this.addText(container, x, y, label, {
      fontSize: '8px', color: '#888888', fontFamily: 'monospace',
    });
    y += 12;

    // Value text (centered between arrows)
    const arrowW = 22;
    const valX = x + arrowW + 4;
    const valW = w - arrowW * 2 - 8;
    this.addText(container, valX, y + 1, value, {
      fontSize: '9px', color: '#ffffff', fontFamily: 'monospace',
      fixedWidth: valW,
    }).setOrigin(0, 0);

    // Left arrow
    this.addButton(container, x, y - 1, arrowW, 16, '◄', onPrev);
    // Right arrow
    this.addButton(container, x + w - arrowW, y - 1, arrowW, 16, '►', onNext);

    return y + 22;
  }

  /** Add a checkbox toggle row. Returns next Y. */
  private addCheckbox(
    container: Phaser.GameObjects.Container,
    x: number, y: number,
    label: string, checked: boolean,
    onToggle: () => void,
  ): number {
    const cb = this.scene.add.rectangle(x + 6, y + 6, 12, 12,
      checked ? 0x44cc44 : 0x333355,
    ).setStrokeStyle(1, 0x666688).setInteractive({ useHandCursor: true });
    cb.on('pointerdown', () => {
      if (!this.isInVisibleArea(cb)) return;
      SoundManager.play('click');
      onToggle();
    });
    container.add(cb);

    this.addText(container, x + 16, y, label, {
      fontSize: '9px', color: '#cccccc', fontFamily: 'monospace',
    });

    return y + 20;
  }

  // ══════════════════════════════════════════
  //  FILE TAB
  // ══════════════════════════════════════════

  // ══════════════════════════════════════════
  //  FILE SECTION
  // ══════════════════════════════════════════

  private buildFileSection(c: Phaser.GameObjects.Container, y: number): number {
    const { propsX, propsW } = this.opts;

    this.addText(c, propsX + 10, y, 'EXPORT / IMPORT', {
      fontSize: '11px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    });
    y += 22;

    this.addButton(c, propsX + 10, y, propsW - 20, 26, 'Export File', () => this.callbacks.exportMap());
    y += 30;
    this.addButton(c, propsX + 10, y, propsW - 20, 26, 'Copy JSON', () => this.callbacks.exportClipboard());
    y += 30;
    this.addButton(c, propsX + 10, y, propsW - 20, 26, 'Import File', () => this.callbacks.importMap());
    y += 30;
    this.addButton(c, propsX + 10, y, propsW - 20, 26, 'Paste JSON', () => this.callbacks.importClipboard());
    y += 32;
    this.addButton(c, propsX + 10, y, propsW - 20, 26, 'Clear All', () => this.callbacks.clearAll());
    y += 40;

    // Procedural generation
    this.addText(c, propsX + 10, y, 'GENERATE MAP', {
      fontSize: '11px', color: '#ffcc00', fontFamily: 'monospace', fontStyle: 'bold',
    });
    y += 18;
    this.addText(c, propsX + 10, y, 'Complexity:', { fontSize: '10px', color: '#aaaaaa', fontFamily: 'monospace' });
    y += 14;
    this.complexityText = this.addText(c, propsX + 10, y, this.opts.complexityLabel, {
      fontSize: '11px', color: '#ffffff', fontFamily: 'monospace',
    });
    this.addButton(c, propsX + 100, y - 2, 40, 18, '◄', () => this.callbacks.cycleComplexity(-1));
    this.addButton(c, propsX + 145, y - 2, 40, 18, '►', () => this.callbacks.cycleComplexity(1));
    y += 20;
    this.addText(c, propsX + 10, y, 'Resources:', { fontSize: '10px', color: '#aaaaaa', fontFamily: 'monospace' });
    y += 14;
    this.richnessText = this.addText(c, propsX + 10, y, this.opts.richnessLabel, {
      fontSize: '11px', color: '#ffffff', fontFamily: 'monospace',
    });
    this.addButton(c, propsX + 100, y - 2, 40, 18, '◄', () => this.callbacks.cycleRichness(-1));
    this.addButton(c, propsX + 145, y - 2, 40, 18, '►', () => this.callbacks.cycleRichness(1));
    y += 20;
    this.addText(c, propsX + 10, y, 'Hazards:', { fontSize: '10px', color: '#aaaaaa', fontFamily: 'monospace' });
    y += 14;
    this.hazardsText = this.addText(c, propsX + 10, y, this.opts.hazardsLabel, {
      fontSize: '11px', color: '#ffffff', fontFamily: 'monospace',
    });
    this.addButton(c, propsX + 100, y - 2, 40, 18, '◄', () => this.callbacks.cycleHazards(-1));
    this.addButton(c, propsX + 145, y - 2, 40, 18, '►', () => this.callbacks.cycleHazards(1));
    y += 22;
    this.addButton(c, propsX + 10, y, propsW - 20, 28, 'Generate', () => this.callbacks.generateProceduralMap());
    y += 40;

    // Save / Campaign actions
    if (this.opts.campaignLevelId) {
      this.addButton(c, propsX + 10, y, propsW - 20, 26, t('editor.saveToCampaign'), () => this.callbacks.saveToCampaign());
      y += 30;
      this.addButton(c, propsX + 10, y, propsW - 20, 26, t('editor.resetToFactory'), () => this.callbacks.resetToFactoryMap());
      y += 30;
    } else {
      this.addButton(c, propsX + 10, y, propsW - 20, 26, 'Save to Maps', () => this.callbacks.saveToMapList());
      y += 32;
    }

    return y;
  }

  // ══════════════════════════════════════════
  //  UI HELPERS
  // ══════════════════════════════════════════

  private addText(
    container: Phaser.GameObjects.Container,
    x: number, y: number, text: string,
    style: Phaser.Types.GameObjects.Text.TextStyle,
  ): Phaser.GameObjects.Text {
    const t = this.scene.add.text(x, y, text, style);
    container.add(t);
    return t;
  }

  private addButton(
    container: Phaser.GameObjects.Container,
    x: number, y: number, w: number, h: number,
    label: string, onClick: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.scene.add.container(x + w / 2, y + h / 2);
    const gfx = this.scene.add.graphics();
    drawTechRect(gfx, -w / 2, -h / 2, w, h, TECH.COLOR, 0.6, 1);
    btn.add(gfx);

    const txt = this.scene.add.text(0, 0, label, {
      fontSize: '10px', color: '#cccccc', fontFamily: 'monospace',
    }).setOrigin(0.5);
    btn.add(txt);

    const hitArea = this.scene.add.rectangle(0, 0, w, h, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    btn.add(hitArea);

    hitArea.on('pointerover', () => {
      gfx.clear();
      drawTechRect(gfx, -w / 2, -h / 2, w, h, TECH.HOVER, 1, 1.5);
      txt.setColor('#ffffff');
    });
    hitArea.on('pointerout', () => {
      gfx.clear();
      drawTechRect(gfx, -w / 2, -h / 2, w, h, TECH.COLOR, 0.6, 1);
      txt.setColor('#cccccc');
    });
    hitArea.on('pointerdown', () => {
      if (!this.isInVisibleArea(hitArea)) return;
      SoundManager.play('click');
      onClick();
    });

    btn.setDepth(10);
    container.add(btn);
    return btn;
  }

  /** Check if a game object's world Y is within the visible tab content area. */
  private isInVisibleArea(obj: Phaser.GameObjects.GameObject): boolean {
    const bounds = (obj as unknown as Phaser.GameObjects.Components.GetBounds).getBounds?.();
    if (!bounds) return true;
    return bounds.y >= CONTENT_Y - 5 && bounds.y < this.opts.canvasH + 5;
  }

  /** Destroy all panel objects. */
  destroy(): void {
    this.container.destroy(true);
    this.maskShape.destroy();
  }
}
